import { create } from "@bufbuild/protobuf";
import { ArchiveReason, AssetState as AssetStateEnum } from "#gen/asset/v1/asset_pb.js";
import { type PaginationArguments, type PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import { FolderFilterSchema, FolderService, type Folder } from "#gen/folder/v1/folders_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { AssetState } from "#lib/api/bundles.js";
import { handleError } from "#lib/api/error.js";
import type { Filter } from "#lib/api/pagination.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface FolderList {
	folders: Folder[];
	pageInfo?: PaginationPageInfo;
}

interface ValidationResponse {
	valid: boolean;
	errorMessage?: string;
}

function toStateProto(state: string): AssetStateEnum {
	switch (state) {
		case AssetState.DRAFT:
			return AssetStateEnum.DRAFT;
		case AssetState.ACTIVE:
			return AssetStateEnum.ACTIVE;
		case AssetState.ARCHIVED:
			return AssetStateEnum.ARCHIVED;
		default:
			return AssetStateEnum.UNSPECIFIED;
	}
}

export const WORKSPACE_ROOT_NAME = "workspace_root";
export const ASSETS_FOLDER_PATH = ".assets";

/**
 * Navigates into a folder. A folder already on the path truncates back to it rather than being
 * appended a second time.
 */
export function enterFolder(folderPath: Folder[], folder: Folder): Folder[] {
	const index = folderPath.findIndex(f => f.id === folder.id);
	if (index === -1) return [...folderPath, folder];
	return index === folderPath.length - 1 ? folderPath : folderPath.slice(0, index + 1);
}

export function isDescendantPath(path: string | undefined, ancestorPath: string | undefined): boolean {
	if (!path || !ancestorPath) {
		return false;
	}
	return path.startsWith(`${ancestorPath}/`);
}

export function getFolderName(folder: Folder): string {
	if (folder.name === WORKSPACE_ROOT_NAME || folder.path === ASSETS_FOLDER_PATH) {
		return "Workspace root";
	}
	return folder.name;
}

export class FolderClient {
	private client: Client<typeof FolderService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.folders || "/", FolderService, options);
	}

	async getFolder(id: string): Promise<Folder> {
		try {
			const res = await this.client.getFolder({ id });
			return res.folder!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getFoldersInPath(orgId: string, workspaceId: string, path: string): Promise<Folder[]> {
		try {
			const res = await this.client.getFoldersInPath({
				orgId,
				workspaceId,
				path,
			});
			return res.folders;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listFolders(orgId: string, workspaceId: string, path?: string, filter?: Filter, paginate?: PaginationArguments, options?: CallOptions): Promise<FolderList> {
		try {
			const res = await this.client.listFolders(
				{
					orgId,
					workspaceId,
					paginate,
					filter: create(FolderFilterSchema, {
						path,
						namePrefix: filter?.namePrefix,
						creatorIds: filter?.creators?.map(c => c.id),
						tags: filter?.tags,
						states: filter?.states?.map(toStateProto),
						includeDescendants: filter?.includeDescendants,
					}),
				},
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createFolder(parent: Folder, name: string, description?: string): Promise<Folder> {
		try {
			const res = await this.client.createFolder({
				orgId: parent.orgId,
				workspaceId: parent.workspaceId,
				parentPath: parent.path,
				name,
				description,
			});
			return res.folder!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateFolder(folder: Folder, name: string, description?: string): Promise<Folder> {
		try {
			const res = await this.client.updateFolder({
				id: folder.id,
				name,
				description,
			});
			return res.folder!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveFolder(folder: Folder): Promise<Folder> {
		try {
			const res = await this.client.archiveFolder({
				id: folder.id,
				archiveReason: ArchiveReason.USER_ARCHIVAL,
			});
			return res.folder!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async archiveFolders(folders: Folder[]): Promise<Folder[]> {
		if (folders.length === 0) {
			return [];
		}
		if (folders.length === 1) {
			return [await this.archiveFolder(folders[0])];
		}
		try {
			const res = await this.client.archiveFolders({
				ids: folders.map(f => f.id),
				archiveReason: ArchiveReason.USER_ARCHIVAL,
			});
			return res.folders!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreFolder(folder: Folder): Promise<Folder> {
		try {
			const res = await this.client.restoreFolder({ id: folder.id });
			return res.folder!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async restoreFolders(folders: Folder[]): Promise<Folder[]> {
		if (folders.length === 0) {
			return [];
		}
		if (folders.length === 1) {
			return [await this.restoreFolder(folders[0])];
		}
		try {
			const res = await this.client.restoreFolders({
				ids: folders.map(f => f.id),
			});
			return res.folders!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteFolder(id: string): Promise<void> {
		try {
			await this.client.deleteFolder({ id });
		} catch (e) {
			throw handleError(e);
		}
	}

	async deleteFolders(ids: string[]): Promise<void> {
		if (ids.length === 0) {
			return;
		}
		if (ids.length === 1) {
			return this.deleteFolder(ids[0]);
		}
		try {
			await this.client.deleteFolders({ ids });
		} catch (e) {
			throw handleError(e);
		}
	}

	async moveFolder(folder: Folder, to: Folder, ignoreAnalytics?: boolean): Promise<Folder> {
		try {
			const res = await this.client.moveFolder({
				id: folder.id,
				destination: {
					orgId: to.orgId,
					workspaceId: to.workspaceId,
					path: to.path,
				},
				ignoreAnalytics,
			});
			return res.folder!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async moveFolders(folders: Folder[], to: Folder, ignoreAnalytics?: boolean): Promise<Folder[]> {
		if (folders.length === 0) {
			return [];
		}
		if (folders.length === 1) {
			return [await this.moveFolder(folders[0], to, ignoreAnalytics)];
		}
		try {
			const res = await this.client.moveFolders({
				ids: folders.map(f => f.id),
				destination: {
					orgId: to.orgId,
					workspaceId: to.workspaceId,
					path: to.path,
				},
				ignoreAnalytics: ignoreAnalytics,
			});
			return res.folders!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async validateFolderName(orgId: string, workspaceId: string, parentPath: string, name: string): Promise<ValidationResponse> {
		try {
			const res = await this.client.validateFolderName({
				orgId,
				workspaceId,
				parentPath,
				name,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}
